import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/** Confirms a valid native form without replacing its submission or validation. */
export function ConfirmSubmitButton({
  children,
  title,
  description,
  confirmLabel,
  cancelLabel,
  processing = false,
  disabled = false,
  details,
}: {
  children: ReactNode;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  processing?: boolean;
  disabled?: boolean;
  details?: (values: FormData) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState<ReactNode>(null);
  const form = useRef<HTMLFormElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const approved = useRef(false);

  useEffect(() => {
    const target = trigger.current?.closest("form");
    if (!target) return;
    const confirmSubmission = (event: SubmitEvent) => {
      if (event.target !== target || approved.current) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      trigger.current?.click();
    };
    target.addEventListener("submit", confirmSubmission, true);
    return () => target.removeEventListener("submit", confirmSubmission, true);
  }, []);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next, event) => {
        if (next) {
          const target = trigger.current?.closest("form");
          if (disabled || processing || !target?.reportValidity()) {
            event.cancel();
            return;
          }
          form.current = target;
          setReview(details?.(new FormData(target)) ?? null);
        }
        setOpen(next);
      }}
    >
      <AlertDialogTrigger
        ref={trigger}
        render={<Button type="button" variant="destructive" />}
        disabled={disabled || processing}
      >
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {review}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={processing}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            variant="destructive"
            disabled={processing}
            onClick={() => {
              const target = form.current;
              if (!target?.isConnected || processing) return;
              if (target.reportValidity()) {
                approved.current = true;
                try {
                  target.requestSubmit();
                } finally {
                  approved.current = false;
                }
              }
              setOpen(false);
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
